import React, { useState, useEffect } from "react";
import { motion } from 'framer-motion';
import {
  Box, Button, Paper, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Grid, CircularProgress, Snackbar, Alert, TextField, useTheme, InputAdornment, Card, CardContent
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "@mui/system";
import { Clock } from 'lucide-react';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { prGetOrderAction, prUpdateOrderAction, clearprUpdateOrderData } from "../../store/actions";
import { Pending, Factory, AssignmentTurnedInOutlined, LocalShipping } from '@mui/icons-material';

const statusColors = {
  Pending: { borderColor: "#FF0000", textColor: "#FF0000" },
  Progress: { borderColor: "#FFA500", textColor: "#FFA500" },
  Manufactured: { borderColor: "#FFFF00", textColor: "#FFFF00" },
  Tested: { borderColor: "#008000", textColor: "#008000" },
  Delivered: { borderColor: "#0000FF", textColor: "#0000FF" },
};

const statusOptions = ["Pending", "Progress", "Manufactured", "Tested", "Delivered"];

const isStatusDisabled = (currentStatus, optionStatus) => {
  switch (currentStatus) {
    case "Pending":
      return false;
    case "Progress":
      return optionStatus === "Pending";
    case "Manufactured":
      return ["Pending", "Progress"].includes(optionStatus);
    case "Tested":
      return ["Pending", "Progress", "Manufactured"].includes(optionStatus);
    case "Delivered":
      return ["Pending", "Progress", "Manufactured", "Tested"].includes(optionStatus);
    default:
      return false;
  }
};

const DashboardCard = ({ title, value, Icon, color, delay, onCardClick, isActive }) => {
  const theme = useTheme();

  const handleClick = () => {
    if (typeof onCardClick === 'function') {
      onCardClick(title);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
      <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }} style={{ borderRadius: 8, overflow: 'hidden' }}>
        <Paper
          onClick={handleClick}
          sx={{
            p: 3,
            borderRadius: 5,
            cursor: 'pointer',
            transition: 'all 0.5s',
            background: isActive ? '#2D60FF' : '#EAEAEA',
            color: isActive ? 'white' : 'inherit',
            position: 'relative',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '200%',
              height: '100%',
              background: 'linear-gradient(90deg, #1814F3 0%, #1814F3 100%)',
              zIndex: 0,
              transform: 'translateX(-100%)',
              transition: 'transform 0.5s ease-in-out',
            },
            '&:hover:before': { transform: 'translateX(0)' },
            '&:hover *': { color: 'white !important' },
            '& *': { position: 'relative', zIndex: 1 },
          }}
        >
          <Box display="flex" alignItems="center">
            <Box sx={{ p: 1.5, borderRadius: 15, backgroundColor: color, transition: 'transform 0.2s' }}>
              <Icon style={{ height: 24, width: 24, color: 'white' }} />
            </Box>
            <Box ml={2}>
              <Typography variant="body2" sx={{ color: isActive ? 'white' : '#718EBF', fontFamily: 'Inter' }}>{title}</Typography>
              <Typography variant="h5" fontWeight="600" color="textPrimary">{value}</Typography>
            </Box>
          </Box>
        </Paper>
      </motion.div>
    </motion.div>
  );
};

const ProductionOrdersManagement = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [selectedStatus, setSelectedStatus] = useState("Pending");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [searchClientId, setSearchClientId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);


  const { successData, loading } = useSelector((state) => state.prGetOrderReducer);
  const { successData: updateSuccessData, loading: updateLoading, error: updateError, errorInfo } = useSelector((state) => state.prUpdateOrderReducer);
  const orders = successData?.data || [];

  useEffect(() => {
    dispatch(prGetOrderAction());
  }, [dispatch]);

  const handleEditClick = (order) => {
    setSelectedOrder(order);
    setSelectedStatus(order.status);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedOrder(null);
  };

  const handleUpdate = () => {
    if (selectedOrder && selectedStatus) {
      const updateData = {
        id: selectedOrder.id,
        status: selectedStatus,
        quantity: selectedOrder.quantity,
      };

      // Dispatch the update action
      dispatch(prUpdateOrderAction({
        endPoint: `pr-manager/order/${selectedOrder.id}`,
        ...updateData,
      }));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  useEffect(() => {
    if (updateSuccessData) {
      setSnackbarMessage("Order status updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleClose();
      dispatch(prGetOrderAction());
      dispatch(clearprUpdateOrderData());
    }

    if (updateError) {
      setSnackbarMessage("Failed to update order status: " + (errorInfo?.message));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      dispatch(clearprUpdateOrderData());
    }
  }, [updateSuccessData, updateError, dispatch]);


  const filteredOrders = orders
    .filter((order) => order.status === selectedStatus)
    .filter((order) =>
      order.id.toString().includes(searchClientId) ||
      order.client_name.toLowerCase().includes(searchClientId.toLowerCase())
    )
    .sort((a, b) => a.Machine?.name.localeCompare(b.Machine?.name));

  const getCategoryCount = (category) => {
    return orders.filter(order => order.status === category).length;
  };

  const renderCellContent = (item, columnName) => {
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      try {
        return new Date(dateString).toISOString().split('T')[0];
      } catch (error) {
        return '-';
      }
    };

    const calculateAlert = () => {
      const status = item.status || selectedStatus;
      switch (status.toLowerCase()) {
        case 'pending':
          return { text: 'Pending', color: '#FF0000' };
        case 'progress':
          return { text: 'In Progress', color: '#FFA500' };
        case 'manufactured':
          return { text: 'Manufactured', color: '#FFD700' };
        case 'tested':
          return { text: 'Tested', color: '#008000' };
        case 'delivered':
          return { text: 'Delivered', color: '#0000FF' };
        default:
          return { text: 'Unknown', color: '#000000' };
      }
    };

    switch (columnName) {
      case 'Product Name':
        return item.Machine?.name || '-';
      case 'Client Name':
        return item.client_name || '-';
      case 'Order ID':
        return item.id || '-';
      case 'Quantity':
        return item.quantity || '-';
      case 'Status':
        const alertInfo = calculateAlert();
        return (
          <Box
            sx={{
              border: `1px solid ${alertInfo.color}`,
              padding: '6px 10px',
              borderRadius: '50px',
              display: 'inline-block',
              fontWeight: 'medium',
              color: alertInfo.color,
            }}
          >
            {item.status || alertInfo.text}
          </Box>
        );
      default:
        return '-';
    }
  };

  const getEmptyStateMessage = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'No pending orders available';
      case 'progress':
        return 'No orders currently in progress';
      case 'manufactured':
        return 'No manufactured orders available';
      case 'tested':
        return 'No tested orders available';
      case 'delivered':
        return 'No delivered orders available';
      default:
        return 'No data available';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pending':
        return Pending;
      case 'Progress':
        return Clock;
      case 'Manufactured':
        return Factory;
      case 'Tested':
        return AssignmentTurnedInOutlined;
      case 'Delivered':
        return LocalShipping;
      default:
        return Pending;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
        return "#ed6c02";
      case 'Progress':
        return "#9c27b0";
      case 'Manufactured':
        return "#1976d2";
      case 'Tested':
        return "#2e7d32";
      case 'Delivered':
        return "#1976d2";
      default:
        return "#ed6c02";
    }
  };

  return (
    <motion.div initial="hidden" animate="visible" style={{ perspective: 1000 }}>
      <Box sx={{ width: '88%', mb: { xs: '10%', sm: '0%' }, mt: { xs: '25%', sm: '15%', md: '10%' }, ml: { xs: '5%', sm: '0%', md: '3%' } }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3, ml: { xs: 0, sm: 0, md: 10 }, mt: { xs: 10, sm: 15, md: 10 } }}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h5"
              fontWeight="bold"
              color="text.primary"
              gutterBottom
              sx={{
                fontSize: { xs: "1.5rem", sm: "1.5rem", md: "1.8rem" },
                textAlign: { xs: "left", sm: "left" },
                ml: { xs: 0, sm: 0, md: '24%' },
              }}
            >
              Orders Management
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              variant="outlined"
              placeholder="Search by Order ID"
              value={searchClientId}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                ml: { xs: 0, sm: 0, md: 'auto' },
                width: { xs: '100%', sm: '100%', md: '250px' },
              }}
              onChange={(e) => setSearchClientId(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
        </Grid>
        {!isMobile && (
          <Grid container spacing={3} sx={{ ml: { xs: 3, sm: '13%', md: '20%' }, width: '85%' }}>
            {statusOptions.map((status, index) => (
              <Grid item key={index} xs={12} sm={6} md={4} lg={2.4} sx={{ mb: 2 }}>
                <DashboardCard
                  title={status}
                  value={getCategoryCount(status)}
                  Icon={getStatusIcon(status)}
                  color={getStatusColor(status)}
                  delay={0.1 * (index + 1)}
                  onCardClick={() => setSelectedStatus(status)}
                  isActive={selectedStatus === status}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {isMobile ? (
          <>
            {/* Add a filter icon for mobile */}
            <Box sx={{ mb: 2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight="bold">
                Orders
              </Typography>
              <Button
                variant="outlined"
                startIcon={<FilterAltIcon />}
                onClick={() => setFilterOpen(!filterOpen)}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: '#fff',
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                Filter
              </Button>
            </Box>

            {filterOpen && (
              <Box sx={{ mb: 2, px: 2 }}>
                <Select
                  fullWidth
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  displayEmpty
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Status
                  </MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            <Grid container spacing={2}>
              {filteredOrders.map((order) => (
                <Grid item xs={12} key={order.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold">
                        {order.Machine?.name || "Unknown Product"}
                      </Typography>

                      <Box mt={1}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Client Name: {order.client_name || "Unknown"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Order ID: {order.id}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Quantity: {order.quantity}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Status: <span style={{
                                color: (() => {
                                  const status = order.status.toLowerCase();
                                  switch (status) {
                                    case 'pending':
                                      return '#FF0000';
                                    case 'progress':
                                      return '#FFA500';
                                    case 'manufactured':
                                      return '#FFD700';
                                    case 'tested':
                                      return '#008000';
                                    case 'delivered':
                                      return '#0000FF';
                                    default:
                                      return '#000000';
                                  }
                                })(),
                                border: `1px solid ${(() => {
                                  const status = order.status.toLowerCase();
                                  switch (status) {
                                    case 'pending':
                                      return '#FF0000';
                                    case 'progress':
                                      return '#FFA500';
                                    case 'manufactured':
                                      return '#FFD700';
                                    case 'tested':
                                      return '#008000';
                                    case 'delivered':
                                      return '#0000FF';
                                    default:
                                      return '#000000';
                                  }
                                })()}`,
                                padding: '2px 6px',
                                borderRadius: '14px',
                                display: 'inline-block',
                              }}>
                                {order.status}
                              </span>
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleEditClick(order)}
                        sx={{ mt: 2 }}
                      >
                        Update
                      </Button>
                    </CardContent>
                  </Card>

                </Grid>
              ))}
            </Grid>
          </>
        ) : (


          <Grid
            container
            spacing={2}
            sx={{
              mt: '5%',
              ml: { md: '24%', xs: 0 },
              width: { md: '75%', xs: '100%' },
              px: 2,
            }}
          >
            {filteredOrders.length === 0 ? (
              <Grid item xs={12} width="100%">
                <Paper sx={{ padding: 2, textAlign: 'center' }}>
                  {getEmptyStateMessage(selectedStatus)}
                </Paper>
              </Grid>
            ) : (
              filteredOrders.map((order) => (
                <Grid item xs={12} key={order.id}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {/* Product Name */}
                    <Typography
                      sx={{
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        color: '#1976d2',
                        width: '200px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {renderCellContent(order, 'Product Name')}
                    </Typography>

                    {/* Order Details */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        flex: 1,
                        px: 2,
                        width: 'calc(100% - 200px)',
                      }}
                    >
                      {[
                        { label: 'Client', field: 'Client Name' },
                        { label: 'Order ID', field: 'Order ID' },
                        { label: 'Quantity', field: 'Quantity' },
                        { label: '', field: 'Status' },
                      ].map((item) => (
                        <Box key={item.label} sx={{ display: 'flex', flexDirection: 'column', width: '150px' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#1976d2',
                              fontWeight: 500,
                              width: '100px',
                              textAlign: 'center',
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              width: '120px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textAlign: 'center',
                            }}
                          >
                            {renderCellContent(order, item.field)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Update Button */}
                    <Box sx={{ marginLeft: '5%' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          borderRadius: 2,
                          minWidth: 80,
                          background: 'linear-gradient(to right, #3f51b5, #2196f3)',
                        }}
                        onClick={() => handleEditClick(order)}
                      >
                        Update
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))
            )}
          </Grid>
        )}

        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>Edit Order Status</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Client: {selectedOrder?.client_name}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Order ID: {selectedOrder?.id}
            </Typography>
            <Select
              fullWidth
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              sx={{ mt: 2 }}
            >
              {statusOptions.map((status) => (
                <MenuItem
                  key={status}
                  value={status}
                  disabled={isStatusDisabled(selectedOrder?.status, status)}
                >
                  {status}
                </MenuItem>
              ))}
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              variant="contained"
              color="primary"
              disabled={updateLoading}
            >
              {updateLoading ? <CircularProgress size={24} /> : "Update"}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </motion.div>

  );
};

export default ProductionOrdersManagement;